import { useState } from 'react';

/**
 * Per-dashboard tab persistence. Caller passes the allowed tab keys (`as const` tuple
 * narrows them into a literal-string union) and the default; the hook returns
 * `[activeTab, setActiveTab]`. Selections persist across refreshes via localStorage
 * keyed by `storageKey`.
 *
 * Unknown / stale / legacy values are remapped by the optional `migrate` callback —
 * useful when a tab gets renamed (e.g., the old "Approved" key on the Originator
 * dashboard maps to the new "Published").
 *
 * Used by:
 *   - WorkflowList — Originator dashboard (Draft / Pending / PendingPublishing / Published / Rejected)
 *   - ApproverDashboard — Approver dashboard (published / approved / queue / recent)
 */
export function useRememberedTab<T extends string>(
  storageKey: string,
  allowedTabs: readonly T[],
  defaultTab: T,
  migrate?: (raw: string) => T | undefined,
): [T, (next: T) => void] {
  const [activeTab, setActiveTab] = useState<T>(() => {
    if (typeof window === 'undefined') return defaultTab;
    const saved = window.localStorage.getItem(storageKey);
    if (saved == null) return defaultTab;
    if ((allowedTabs as readonly string[]).includes(saved)) return saved as T;
    return migrate?.(saved) ?? defaultTab;
  });

  const setAndRemember = (next: T) => {
    setActiveTab(next);
    if (typeof window !== 'undefined') window.localStorage.setItem(storageKey, next);
  };

  return [activeTab, setAndRemember];
}
